import React, { useEffect } from 'react';
import { 
  Target, Eye, Compass, Sun, Users, Globe, Leaf,
  Zap, Award, Heart, ArrowRight, CheckCircle
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const MisionVisionPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const objectives = [
    {
      icon: Sun,
      title: 'Democratizar la Energía Solar',
      description: 'Hacer que la energía solar sea accesible económicamente para todos los segmentos de la población.'
    },
    {
      icon: Award,
      title: 'Excelencia en Servicio',
      description: 'Mantener los más altos estándares de calidad en productos, instalación y servicio post-venta.'
    },
    {
      icon: Globe,
      title: 'Impacto Ambiental',
      description: 'Contribuir significativamente a la reducción de emisiones de CO2 en Paraguay.'
    },
    {
      icon: Users,
      title: 'Desarrollo de Talento',
      description: 'Formar profesionales especializados en energías renovables en Paraguay.'
    },
    {
      icon: Heart,
      title: 'Responsabilidad Social',
      description: 'Llevar energía limpia a comunidades vulnerables a través de proyectos de impacto social.'
    },
    {
      icon: Zap,
      title: 'Innovación Continua',
      description: 'Estar a la vanguardia de las tecnologías de energía renovable y almacenamiento.'
    }
  ];

  const principles = [
    'Integridad en todas nuestras acciones',
    'Transparencia con clientes y socios',
    'Compromiso con la calidad',
    'Respeto por el medio ambiente',
    'Trabajo en equipo',
    'Mejora continua'
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="mision-vision-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-emerald-800 via-teal-700 to-emerald-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-300 mb-6">
            <Compass className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Nuestro Norte</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Misión y Visión
          </h1>
          
          <p className="text-xl text-emerald-100 max-w-2xl">
            El propósito que nos impulsa y el futuro que queremos construir 
            para Paraguay y las generaciones venideras.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-zinc-900">Nuestra Misión</h2>
              </div>
              
              <p className="text-xl text-zinc-700 leading-relaxed mb-6">
                "Transformar la forma en que Paraguay genera y consume energía, 
                ofreciendo soluciones solares de alta calidad que permitan a 
                hogares, empresas y comunidades alcanzar la independencia energética 
                de manera sostenible y económica."
              </p>

              <p className="text-zinc-600">
                Cada instalación que realizamos es un paso hacia un Paraguay más 
                limpio, más eficiente y más autosuficiente energéticamente.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-4">Lo que hacemos cada día:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Diseñamos sistemas solares personalizados</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Instalamos con los más altos estándares</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Educamos sobre energía renovable</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Brindamos soporte continuo a nuestros clientes</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Contribuimos a comunidades sin acceso a energía</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 bg-white/5 rounded-2xl p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4 text-emerald-400">Paraguay 2030:</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Sun className="w-5 h-5 mt-0.5 text-yellow-400 flex-shrink-0" />
                  <span>20% de energía generada por fuentes solares distribuidas</span>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="w-5 h-5 mt-0.5 text-blue-400 flex-shrink-0" />
                  <span>100,000+ hogares con independencia energética</span>
                </li>
                <li className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 mt-0.5 text-emerald-400 flex-shrink-0" />
                  <span>500,000 toneladas de CO2 evitadas anualmente</span>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 mt-0.5 text-cyan-400 flex-shrink-0" />
                  <span>Paraguay reconocido como líder regional en energía solar</span>
                </li>
              </ul>
            </div>

            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold">Nuestra Visión</h2>
              </div>
              
              <p className="text-xl text-zinc-300 leading-relaxed mb-6">
                "Ser el motor de la transición energética en Paraguay, liderando 
                la adopción de energía solar en todos los sectores y convirtiendo 
                a nuestro país en un referente regional de sostenibilidad."
              </p>

              <p className="text-zinc-400">
                Soñamos con un Paraguay donde cada techo tenga paneles solares, 
                donde la energía limpia sea la norma y no la excepción.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Objetivos Estratégicos
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Las metas que guían nuestras acciones y decisiones
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {objectives.map((obj, index) => {
              const Icon = obj.icon;
              return (
                <div 
                  key={index}
                  className="p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{obj.title}</h3>
                  <p className="text-zinc-600">{obj.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-zinc-900 mb-4">
              Nuestros Principios
            </h2>
            <p className="text-zinc-600">
              Los valores que rigen cada una de nuestras acciones
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {principles.map((principle, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-zinc-700 font-medium">{principle}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Construyamos juntos el futuro
          </h2>
          <p className="text-emerald-100 mb-8">
            Tu decisión de adoptar energía solar nos acerca a nuestra visión
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all"
            >
              Comenzar Ahora
            </button>
            <Link 
              to="/nosotros/sustentabilidad"
              className="px-8 py-4 bg-emerald-800/50 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-all flex items-center gap-2"
            >
              Ver Sustentabilidad
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default MisionVisionPage;
